import {
  Box,
  Button,
  Disclaimer,
  Div,
  H5,
  OutstandingItem,
  P,
  Pill,
  displayCentsInDollars,
  lightTheme,
} from '@vgrubs/components';
import {
  SERVICE_LABELS,
  SOURCE_LABELS,
  STATUS_LABELS,
  formatDay,
} from '../helpers/receivablesLabels';
import { MobileLabel } from './MobileLabel';

type Props = {
  item: OutstandingItem;
  /** Admin only, and only on open items. */
  canResolve: boolean;
  onMarkPaid: (item: OutstandingItem) => void;
  onWriteOff: (item: OutstandingItem) => void;
  onRetry: (item: OutstandingItem) => void;
};

/** Widths shared with the history's TableHeader, so the columns line up. */
// The actions get a quarter of the row so Mark paid and Write off sit side by
// side; Status only ever holds a short pill and a date.
export const ITEM_COLUMNS = [
  { label: 'Date', width: '12%' },
  { label: 'Service', width: '9%' },
  { label: 'What', width: '33%' },
  { label: 'Amount', width: '11%' },
  { label: 'Status', width: '10%' },
  { label: '', width: '25%' },
];

/**
 * One item in a location's history. Only an open item can be closed, and a
 * retry is offered where a failed bank payment sits behind it.
 *
 * Columns on desktop; on a phone a card of "label: value" lines like the
 * receivables list rows, the status as coloured text instead of a pill.
 */
export const OutstandingItemRow = (props: Props) => (
  <Box $mb="8">
    <Div $desktop>
      <DesktopItem {...props} />
    </Div>
    <Div $mobile>
      <MobileItem {...props} />
    </Div>
  </Box>
);

/**
 * Partly paid back by a repayment plan: an open item leads with what it
 * still owes, the figure every total on the page uses; a closed one keeps
 * its amount. A fully repaid one is `paid`, with the plan in its note.
 */
const owedNow = (item: OutstandingItem) =>
  item.status === 'open' ? item.amount - (item.repaidAmount ?? 0) : item.amount;

const statusColor = (item: OutstandingItem) =>
  item.status === 'open'
    ? lightTheme.primary
    : item.status === 'paid'
    ? lightTheme.green
    : lightTheme.lightGray;

const sourceLine = (item: OutstandingItem) =>
  `${SOURCE_LABELS[item.source] ?? item.source}${
    item.clientPaymentId ? ` - payment #${item.clientPaymentId}` : ''
  }`;

const DesktopItem = ({ item, ...actions }: Props) => {
  const open = item.status === 'open';
  const repaid = item.repaidAmount ?? 0;

  return (
    <Div $flex $alignCenter $gap="24px">
      <Div $width={ITEM_COLUMNS[0].width}>
        <P $m="0">{formatDay(item.createdAt)}</P>
      </Div>

      <Div $width={ITEM_COLUMNS[1].width}>
        <P $m="0">{SERVICE_LABELS[item.service]}</P>
      </Div>

      <Div $width={ITEM_COLUMNS[2].width}>
        <P $m="0">{item.description}</P>
        <Disclaimer $m="0" $color={lightTheme.lightGray}>
          {sourceLine(item)}
        </Disclaimer>
        {item.note && <Disclaimer $m="0">Note: {item.note}</Disclaimer>}
      </Div>

      <Div $width={ITEM_COLUMNS[3].width}>
        <H5 $m="0" $color={open ? lightTheme.primary : undefined}>
          {displayCentsInDollars(owedNow(item))}
        </H5>
        {repaid > 0 && open && (
          <Disclaimer $m="0" $color={lightTheme.lightGray}>
            of {displayCentsInDollars(item.amount)}
          </Disclaimer>
        )}
        {repaid > 0 && (
          <Disclaimer $m="0" $color={lightTheme.green}>
            {displayCentsInDollars(repaid)} repaid
          </Disclaimer>
        )}
      </Div>

      <Div $width={ITEM_COLUMNS[4].width}>
        <Pill $background={statusColor(item)}>
          <Disclaimer $m="0" $color={lightTheme.white}>
            {STATUS_LABELS[item.status]}
          </Disclaimer>
        </Pill>
        {item.resolvedAt && (
          <Disclaimer $m="4px 0 0 0">{formatDay(item.resolvedAt)}</Disclaimer>
        )}
      </Div>

      <Div $width={ITEM_COLUMNS[5].width} $flex $column $alignEnd $gap="8px">
        <ItemActions item={item} {...actions} />
      </Div>
    </Div>
  );
};

const MobileItem = ({ item, ...actions }: Props) => {
  const open = item.status === 'open';
  const repaid = item.repaidAmount ?? 0;
  // Grey stays the normal text colour here, as on the list's phone cards:
  // light grey on its own reads as disabled.
  const color = statusColor(item);

  return (
    <Div $flex $column $gap="4px">
      <H5 $m="0 0 4px">{item.description}</H5>
      <Line label="Date">
        <Disclaimer $m="0">{formatDay(item.createdAt)}</Disclaimer>
      </Line>
      <Line label="Service">
        <Disclaimer $m="0">{SERVICE_LABELS[item.service]}</Disclaimer>
      </Line>
      <Line label="Source">
        <Disclaimer $m="0">{sourceLine(item)}</Disclaimer>
      </Line>
      {item.note && (
        <Line label="Note">
          <Disclaimer $m="0">{item.note}</Disclaimer>
        </Line>
      )}
      <Line label="Status">
        <Disclaimer
          $m="0"
          $color={color === lightTheme.lightGray ? undefined : color}
        >
          {STATUS_LABELS[item.status]}
        </Disclaimer>
        {item.resolvedAt && (
          <Disclaimer $m="0" $color={lightTheme.lightGray}>
            {formatDay(item.resolvedAt)}
          </Disclaimer>
        )}
      </Line>
      <Line label="Amount">
        <H5 $m="0" $color={open ? lightTheme.primary : undefined}>
          {displayCentsInDollars(owedNow(item))}
        </H5>
        {repaid > 0 && open && (
          <Disclaimer $m="0" $color={lightTheme.lightGray}>
            of {displayCentsInDollars(item.amount)}
          </Disclaimer>
        )}
        {repaid > 0 && (
          <Disclaimer $m="0" $color={lightTheme.green}>
            {displayCentsInDollars(repaid)} repaid
          </Disclaimer>
        )}
      </Line>
      <Div $flex $column $alignCenter $gap="8px" $mt="8">
        <ItemActions item={item} {...actions} />
      </Div>
    </Div>
  );
};

/** "Label: value", the value wrapping beside the label, never under it. */
const Line = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <Div $flex $alignCenter $gap="4px" $wrap>
    <MobileLabel>{label}</MobileLabel>
    {children}
  </Div>
);

/**
 * Mark paid and Write off, admin only and only on open items, and the retry
 * where a failed bank payment sits behind the item. On a phone the two
 * buttons split the card's width.
 */
const ItemActions = ({
  item,
  canResolve,
  onMarkPaid,
  onWriteOff,
  onRetry,
}: Props) => {
  const open = item.status === 'open';
  return (
    <>
      {open && canResolve && (
        <Div $flex $gap="8px" $wrap $justifyEnd $widthSM="100%">
          <Button
            $small
            $w="100px"
            $wSM="calc(50% - 4px)"
            $selected
            onClick={() => onMarkPaid(item)}
          >
            Mark paid
          </Button>
          <Button
            $small
            $w="100px"
            $wSM="calc(50% - 4px)"
            $gray
            $selected
            onClick={() => onWriteOff(item)}
          >
            Write off
          </Button>
        </Div>
      )}
      {open && item.clientPaymentId && item.source === 'failedAch' && (
        <Disclaimer
          $m="0"
          $pointer
          $underline
          $color={lightTheme.primary}
          onClick={() => onRetry(item)}
        >
          Retry bank payment
        </Disclaimer>
      )}
    </>
  );
};
